import React, { useState } from 'react';
import { FlashcardFace, FlashcardRateButtons } from '@/features/flashcards/FlashcardStudyCard';
import type { FlashcardResult } from './types';
import { dueInDaysForRating } from './dueInDays';
import { PamNote } from './components/PamNote';
import { ContinueButton } from './components/DiagnosticQuestion';

type Rating = FlashcardResult['rating'];

const DAY_WORDS: Record<number, string> = {
  1: 'one day',
  2: 'two days',
  3: 'three days',
  7: 'seven days',
};

function spacingPromise(days: number): string {
  const word = DAY_WORDS[days] ?? `${days} days`;
  return `This card returns in ${word}.`;
}

export interface PublicDiagnosticFlashcardTurnProps {
  cardId: string;
  front: string;
  back: string;
  onComplete: (result: FlashcardResult) => void;
}

export const PublicDiagnosticFlashcardTurn: React.FC<PublicDiagnosticFlashcardTurnProps> = ({
  cardId,
  front,
  back,
  onComplete,
}) => {
  const [flipped, setFlipped] = useState(false);
  const [rated, setRated] = useState<FlashcardResult | null>(null);

  const handleRate = (rating: Rating) => {
    setRated({
      turnId: 5,
      kind: 'flashcard',
      completedAt: new Date().toISOString(),
      cardId,
      rating,
      dueInDays: dueInDaysForRating(rating),
    });
  };

  return (
    <div className="space-y-5 max-w-xl mx-auto" data-testid="public-diagnostic-flashcard">
      <FlashcardFace front={front} back={back} flipped={flipped} onFlip={() => setFlipped(true)} />

      {flipped && !rated && <FlashcardRateButtons onRate={handleRate} />}

      {rated && (
        <div className="space-y-4">
          <p data-testid="flashcard-due-in-days" className="body-base text-neutral-800 font-semibold">
            {spacingPromise(rated.dueInDays)}
          </p>
          <PamNote label="Pam's tip">Nice. Spacing it out is how this sticks.</PamNote>
          <ContinueButton onClick={() => onComplete(rated)} />
        </div>
      )}
    </div>
  );
};
